<template>
    <div class="d-flex flex-column" style="gap: var(--space-5)">
        <div class="d-flex flex-column">
            <DetailList
                v-if="actionPoint.category"
                :header="$t('actionItems.shared.fields.category')"
                :show-bottom-border="true"
            >
                <strong class="value">{{ actionPoint.category }}</strong>
            </DetailList>
            <DetailList
                v-if="actionPoint.assignedTo"
                :header="$t('actionItems.shared.fields.accountable')"
                :show-bottom-border="true"
            >
                <strong class="value">{{ actionPoint.assignedTo }}</strong>
            </DetailList>
            <DetailList
                v-if="actionPoint.action_point"
                :header="$t('actionItems.shared.fields.actionItem')"
                :show-bottom-border="true"
            >
                <strong class="value">{{ actionPoint.action_point }}</strong>
            </DetailList>
            <DetailList
                v-if="actionPoint.priority"
                :header="$t('actionItems.shared.fields.priority')"
                :show-bottom-border="true"
            >
                <span class="value">
                    <StatusDot
                        :status="getPriorityStatus(actionPoint.priority)"
                        :tooltip="actionPoint.priority || ''"
                    />
                    <strong style="margin-left: var(--space-2)">{{
                        formatPriority(actionPoint.priority)
                    }}</strong>
                </span>
            </DetailList>
            <DetailList
                v-if="actionPoint.deadline"
                :header="$t('actionItems.shared.fields.deadline')"
                :show-bottom-border="true"
            >
                <span class="value"
                    ><strong>{{
                        formatDate(actionPoint.deadline)
                    }}</strong></span
                >
            </DetailList>
            <DetailList
                v-if="actionPoint.progress"
                :header="$t('actionItems.shared.fields.progress')"
                :show-bottom-border="true"
            >
                <span class="value">
                    <i
                        :class="[
                            'fi fi-lg',
                            getProgressIcon(actionPoint.progress),
                            getProgressIconClass(actionPoint.progress),
                        ]"
                        :title="actionPoint.progress || ''"
                    ></i>
                    <strong style="margin-left: var(--space-2)">{{
                        formatProgress(actionPoint.progress)
                    }}</strong>
                </span>
            </DetailList>
            <DetailList
                v-if="actionPoint.completion_date"
                :header="$t('actionItems.shared.fields.completionDate')"
                :show-bottom-border="true"
            >
                <span class="value"
                    ><strong>{{
                        formatDate(actionPoint.completion_date)
                    }}</strong></span
                >
            </DetailList>
            <DetailList
                v-if="actionPoint.notes"
                :header="$t('fields.notes.label')"
                :show-bottom-border="true"
            >
                <strong class="value">{{ actionPoint.notes }}</strong>
            </DetailList>
            <div v-if="hasFiles" class="image-card">
                <AdvancedUploadField
                    v-model="files"
                    :multiple="true"
                    :disabled="true"
                    :hide-upload-field="true"
                    :hide-delete-button="true"
                />
            </div>
        </div>
    </div>
</template>

<script>
import AdvancedUploadField from '@/components/fields/AdvancedUploadField.vue';
import DetailList from '@/components/DetailList.vue';
import StatusDot from '@/components/StatusDot.vue';
import { formatDisplayDate } from '@/utils/dateUtils';
import {
    getPriorityLabel,
    getProgressIcon,
    getProgressIconClass,
    getProgressLabel,
} from '@/utils/actionItemDisplay';

export default {
    name: 'ViewActionPoint',
    components: {
        AdvancedUploadField,
        DetailList,
        StatusDot,
    },
    props: {
        actionPoint: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            files: [],
        };
    },
    computed: {
        hasFiles() {
            return Array.isArray(this.files) && this.files.length > 0;
        },
    },
    watch: {
        actionPoint: {
            immediate: true,
            deep: true,
            handler(actionPoint) {
                this.files = Array.isArray(actionPoint?.files)
                    ? actionPoint.files
                    : [];
            },
        },
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return this.$t('common.fallbacks.notAvailable');
            return formatDisplayDate(dateString);
        },
        formatProgress(progress) {
            return getProgressLabel(progress, this.$t);
        },
        formatPriority(priority) {
            return getPriorityLabel(priority, this.$t);
        },
        getProgressIcon,
        getProgressIconClass,
        getPriorityStatus(priority) {
            if (!priority) return 'success';

            const priorityUpper = String(priority).toUpperCase().trim();

            if (priorityUpper === 'HIGH') {
                return 'danger';
            } else if (priorityUpper === 'MEDIUM') {
                return 'warning';
            } else if (priorityUpper === 'LOW') {
                return 'success';
            }

            return 'success';
        },
    },
};
</script>

<style scoped>
.value {
    font-size: 0.9375rem;
    color: var(--neutral-900);
    display: flex;
    align-items: center;
}

.progress-success {
    color: var(--success);
}

.progress-warning {
    color: var(--warning);
}

.progress-danger {
    color: var(--danger);
}

.image-card {
    margin-top: var(--space-3);
    padding: var(--space-3);
    gap: var(--space-3);
    background-color: var(--neutral-400);
    border-radius: var(--space-3);
}
</style>
